import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 5;
export const ERASER_MAX_ZOOM = 16;
export const CANVAS_VIEWPORT_PADDING = 24;
export const ZOOM_STOPS = [
  0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5, 6, 8, 12, 16,
];

const getCanvasPanSlack = ({ viewportWidth = 0, viewportHeight = 0 }) => ({
  slackX:
    viewportWidth > 0
      ? Math.max(CANVAS_VIEWPORT_PADDING * 4, Math.round(viewportWidth * 0.8))
      : 0,
  slackY:
    viewportHeight > 0
      ? Math.max(CANVAS_VIEWPORT_PADDING * 4, Math.round(viewportHeight * 0.8))
      : 0,
});

export const getCanvasZoomLayout = ({
  viewportWidth = 0,
  viewportHeight = 0,
  fitWidth,
  fitHeight,
  zoom,
  padding = CANVAS_VIEWPORT_PADDING,
}) => {
  const boardWidth = fitWidth * zoom;
  const boardHeight = fitHeight * zoom;
  const padX = Math.max(padding, (viewportWidth - fitWidth) / 2);
  const padY = Math.max(padding, (viewportHeight - fitHeight) / 2);
  const { slackX, slackY } = getCanvasPanSlack({
    viewportWidth,
    viewportHeight,
  });
  return {
    boardWidth,
    boardHeight,
    padX: padX + slackX,
    padY: padY + slackY,
    contentWidth: Math.max(viewportWidth, padX * 2 + boardWidth) + slackX * 2,
    contentHeight:
      Math.max(viewportHeight, padY * 2 + boardHeight) + slackY * 2,
  };
};

export const canvasZoomBoardSx = ({
  padX,
  padY,
  fitWidth,
  fitHeight,
  zoom,
}) => ({
  position: "absolute",
  left: padX,
  top: padY,
  width: fitWidth,
  height: fitHeight,
  transform: `scale(${zoom})`,
  transformOrigin: "0 0",
});

export const layoutBoxToVisual = ({ box, zoom }) => {
  if (!box) return box;
  return {
    ...box,
    x: box.x * zoom,
    y: box.y * zoom,
    width: box.width * zoom,
    height: box.height * zoom,
  };
};

const ZOOM_WILL_CHANGE_MS = 180;
const WHEEL_ZOOM_SENSITIVITY = 0.0015;
const PINCH_ZOOM_SENSITIVITY = 0.0055;
const PINCH_DELTA_PIXEL_MAX = 50;
const WHEEL_LINE_PX = 16;
const WHEEL_PAGE_PX = 800;

const clamp = ({ value, min, max }) => Math.min(max, Math.max(min, value));

const getNormalizedWheelDeltaY = (event) => {
  switch (event.deltaMode) {
    case 1:
      return event.deltaY * WHEEL_LINE_PX;
    case 2:
      return event.deltaY * WHEEL_PAGE_PX;
    default:
      return event.deltaY;
  }
};

const isPinchWheelEvent = (event) =>
  event.deltaMode === 0 &&
  Math.abs(event.deltaY) > 0 &&
  Math.abs(event.deltaY) < PINCH_DELTA_PIXEL_MAX;

const getWheelZoomFactor = (event) => {
  const deltaY = getNormalizedWheelDeltaY(event);
  const sensitivity = isPinchWheelEvent(event)
    ? PINCH_ZOOM_SENSITIVITY
    : WHEEL_ZOOM_SENSITIVITY;
  return Math.exp(-deltaY * sensitivity);
};

const isNativeEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }
  return target.isContentEditable;
};

const getZoomStops = (maxZoom = MAX_ZOOM) =>
  ZOOM_STOPS.filter((stop) => stop <= maxZoom + 0.001);

export const nextZoomStop = ({ zoom, direction, maxZoom = MAX_ZOOM }) => {
  const stops = getZoomStops(maxZoom);
  if (direction > 0) {
    return stops.find((stop) => stop > zoom + 0.001) || maxZoom;
  }
  return [...stops].reverse().find((stop) => stop < zoom - 0.001) || MIN_ZOOM;
};

const scrollBoardPointToCenter = ({ viewport, board, ratioX, ratioY }) => {
  if (!viewport || !board) return;
  const boardRect = board.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  const pointClientX = boardRect.left + ratioX * boardRect.width;
  const pointClientY = boardRect.top + ratioY * boardRect.height;
  const centerX = viewportRect.left + viewport.clientWidth / 2;
  const centerY = viewportRect.top + viewport.clientHeight / 2;
  viewport.scrollLeft += pointClientX - centerX;
  viewport.scrollTop += pointClientY - centerY;
};

export const useCanvasZoom = ({
  viewportRef,
  boardRef,
  resetKey,
  lockRef,
  maxZoomRef,
}) => {
  const zoomRef = useRef(1);
  const pendingScrollRef = useRef(null);
  const lastOriginRef = useRef({ x: null, y: null });
  const pointerOverViewportRef = useRef(false);
  const spaceHeldRef = useRef(false);
  const panDragRef = useRef(null);
  const zoomFlushRafRef = useRef(null);
  const willChangeTimerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [boardLayoutSignature, setBoardLayoutSignature] = useState("0x0");
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  const flushZoomPaint = useCallback(() => {
    zoomFlushRafRef.current = null;
    const next = zoomRef.current;
    setZoom((current) => (Math.abs(current - next) < 0.001 ? current : next));
  }, []);

  const scheduleZoomPaint = useCallback(() => {
    if (zoomFlushRafRef.current != null) return;
    zoomFlushRafRef.current = requestAnimationFrame(flushZoomPaint);
  }, [flushZoomPaint]);

  const markBoardWillChange = useCallback(() => {
    const board = boardRef.current;
    if (board) board.style.willChange = "transform";
    if (willChangeTimerRef.current) clearTimeout(willChangeTimerRef.current);
    willChangeTimerRef.current = setTimeout(() => {
      willChangeTimerRef.current = null;
      if (boardRef.current) boardRef.current.style.willChange = "";
    }, ZOOM_WILL_CHANGE_MS);
  }, [boardRef]);

  const paintZoom = useCallback(
    (nextZoom) => {
      zoomRef.current = nextZoom;
      markBoardWillChange();
      scheduleZoomPaint();
    },
    [markBoardWillChange, scheduleZoomPaint]
  );

  useEffect(
    () => () => {
      if (zoomFlushRafRef.current != null) {
        cancelAnimationFrame(zoomFlushRafRef.current);
      }
      if (willChangeTimerRef.current) clearTimeout(willChangeTimerRef.current);
    },
    []
  );

  const applyZoom = useCallback(
    ({
      nextZoom,
      originClientX,
      originClientY,
      fit = false,
      centerRatio = null,
    }) => {
      const viewport = viewportRef.current;
      const current = zoomRef.current;
      const maxZoom = maxZoomRef?.current ?? MAX_ZOOM;
      const clamped = clamp({
        value: nextZoom,
        min: MIN_ZOOM,
        max: maxZoom,
      });
      if (Math.abs(clamped - current) < 0.001 && !fit && !centerRatio) return;

      if (fit) {
        pendingScrollRef.current = {
          mode: "center",
          ratioX: 0.5,
          ratioY: 0.5,
          requireFitZoom: true,
        };
        zoomRef.current = 1;
        if (Math.abs(current - 1) >= 0.001) {
          paintZoom(1);
          return;
        }
        const board = boardRef.current;
        if (viewport && board?.offsetWidth) {
          scrollBoardPointToCenter({
            viewport,
            board,
            ratioX: 0.5,
            ratioY: 0.5,
          });
          pendingScrollRef.current = null;
        }
        return;
      }

      if (centerRatio) {
        const zoomChanged = Math.abs(clamped - current) >= 0.001;
        if (zoomChanged) {
          pendingScrollRef.current = {
            mode: "center",
            ratioX: centerRatio.x,
            ratioY: centerRatio.y,
          };
          paintZoom(clamped);
          return;
        }
        scrollBoardPointToCenter({
          viewport,
          board: boardRef.current,
          ratioX: centerRatio.x,
          ratioY: centerRatio.y,
        });
        return;
      }

      if (viewport) {
        const rect = viewport.getBoundingClientRect();
        const overViewport =
          originClientX != null &&
          originClientY != null &&
          originClientX >= rect.left &&
          originClientX <= rect.right &&
          originClientY >= rect.top &&
          originClientY <= rect.bottom;
        const clientX = overViewport
          ? originClientX
          : rect.left + viewport.clientWidth / 2;
        const clientY = overViewport
          ? originClientY
          : rect.top + viewport.clientHeight / 2;
        const board = boardRef.current;
        const boardRect = board?.getBoundingClientRect();
        pendingScrollRef.current = {
          originClientX: clientX,
          originClientY: clientY,
          ratioX:
            boardRect && boardRect.width
              ? (clientX - boardRect.left) / boardRect.width
              : 0.5,
          ratioY:
            boardRect && boardRect.height
              ? (clientY - boardRect.top) / boardRect.height
              : 0.5,
        };
      }

      paintZoom(clamped);
    },
    [boardRef, maxZoomRef, paintZoom, viewportRef]
  );

  const onZoomIn = useCallback(() => {
    applyZoom({
      nextZoom: nextZoomStop({
        zoom: zoomRef.current,
        direction: 1,
        maxZoom: maxZoomRef?.current ?? MAX_ZOOM,
      }),
      originClientX: lastOriginRef.current.x,
      originClientY: lastOriginRef.current.y,
    });
  }, [applyZoom, maxZoomRef]);

  const onZoomOut = useCallback(() => {
    applyZoom({
      nextZoom: nextZoomStop({
        zoom: zoomRef.current,
        direction: -1,
        maxZoom: maxZoomRef?.current ?? MAX_ZOOM,
      }),
      originClientX: lastOriginRef.current.x,
      originClientY: lastOriginRef.current.y,
    });
  }, [applyZoom, maxZoomRef]);

  const onFit = useCallback(() => {
    applyZoom({ nextZoom: 1, fit: true });
  }, [applyZoom]);

  const zoomTo = useCallback(
    ({ nextZoom, ratioX = 0.5, ratioY = 0.5 }) => {
      const board = boardRef.current;
      const boardRect = board?.getBoundingClientRect();
      applyZoom({
        nextZoom,
        originClientX: boardRect
          ? boardRect.left + ratioX * boardRect.width
          : undefined,
        originClientY: boardRect
          ? boardRect.top + ratioY * boardRect.height
          : undefined,
      });
    },
    [applyZoom, boardRef]
  );

  const centerOn = useCallback(
    ({ nextZoom, ratioX = 0.5, ratioY = 0.5 }) => {
      applyZoom({
        nextZoom: nextZoom ?? zoomRef.current,
        centerRatio: { x: ratioX, y: ratioY },
      });
    },
    [applyZoom]
  );

  useLayoutEffect(() => {
    zoomRef.current = 1;
    setZoom(1);
    pendingScrollRef.current = {
      mode: "center",
      ratioX: 0.5,
      ratioY: 0.5,
      requireFitZoom: true,
    };
  }, [resetKey]);

  // Board can mount after fit-center was queued (image size arrives later).
  // eslint-disable-next-line
  useLayoutEffect(() => {
    const board = boardRef.current;
    const next = board ? `${board.offsetWidth}x${board.offsetHeight}` : "0x0";
    setBoardLayoutSignature((current) => (current === next ? current : next));
  });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    if (rect.width && rect.height) {
      setViewportSize({ width: rect.width, height: rect.height });
    }
  }, [viewportRef]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setViewportSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height }
      );
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewportRef]);

  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    const viewport = viewportRef.current;
    const board = boardRef.current;
    if (!pending || !viewport || !board) return;
    if (!viewportSize.width || !viewportSize.height) return;
    if (!board.offsetWidth || !viewport.clientWidth) return;
    if (pending.requireFitZoom && Math.abs(zoom - 1) > 0.001) return;
    pendingScrollRef.current = null;
    if (pending.mode === "center") {
      scrollBoardPointToCenter({
        viewport,
        board,
        ratioX: pending.ratioX,
        ratioY: pending.ratioY,
      });
      return;
    }
    const boardRect = board.getBoundingClientRect();
    const pointClientX = boardRect.left + pending.ratioX * boardRect.width;
    const pointClientY = boardRect.top + pending.ratioY * boardRect.height;
    viewport.scrollLeft += pointClientX - pending.originClientX;
    viewport.scrollTop += pointClientY - pending.originClientY;
  }, [
    boardLayoutSignature,
    boardRef,
    resetKey,
    viewportRef,
    viewportSize.height,
    viewportSize.width,
    zoom,
  ]);

  useEffect(() => {
    const onPointerMove = (event) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const inside =
        event.target instanceof Node && viewport.contains(event.target);
      pointerOverViewportRef.current = inside;
      if (!inside) return;
      lastOriginRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [viewportRef]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    viewport.dataset.canvasPan = isPanning
      ? "grabbing"
      : spaceHeld
        ? "grab"
        : "";
    return () => {
      delete viewport.dataset.canvasPan;
    };
  }, [isPanning, spaceHeld, viewportRef]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== "Space") return;
      if (isNativeEditableTarget(event.target)) return;
      const viewport = viewportRef.current;
      const targetInside =
        viewport &&
        event.target instanceof Node &&
        viewport.contains(event.target);
      if (!targetInside && !pointerOverViewportRef.current) return;
      event.preventDefault();
      if (spaceHeldRef.current) return;
      spaceHeldRef.current = true;
      setSpaceHeld(true);
    };
    const onKeyUp = (event) => {
      if (event.code !== "Space") return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    };
    const onBlur = () => {
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [viewportRef]);

  useEffect(() => {
    const endPan = () => {
      if (!panDragRef.current) return;
      panDragRef.current = null;
      setIsPanning(false);
    };
    const onPointerDown = (event) => {
      const viewport = viewportRef.current;
      if (!viewport || !(event.target instanceof Node)) return;
      if (!viewport.contains(event.target)) return;
      if (isNativeEditableTarget(event.target)) return;
      if (lockRef?.current) return;
      const isMiddle = event.button === 1;
      const isSpaceDrag = event.button === 0 && spaceHeldRef.current;
      if (!isMiddle && !isSpaceDrag) return;
      event.preventDefault();
      event.stopPropagation();
      panDragRef.current = {
        lastX: event.clientX,
        lastY: event.clientY,
      };
      setIsPanning(true);
    };
    const onPointerMove = (event) => {
      const drag = panDragRef.current;
      const viewport = viewportRef.current;
      if (!drag || !viewport) return;
      viewport.scrollLeft -= event.clientX - drag.lastX;
      viewport.scrollTop -= event.clientY - drag.lastY;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
    };
    const onAuxClick = (event) => {
      const viewport = viewportRef.current;
      if (!viewport || !(event.target instanceof Node)) return;
      if (!viewport.contains(event.target)) return;
      if (event.button === 1) event.preventDefault();
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endPan);
    window.addEventListener("pointercancel", endPan);
    window.addEventListener("auxclick", onAuxClick, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endPan);
      window.removeEventListener("pointercancel", endPan);
      window.removeEventListener("auxclick", onAuxClick, true);
    };
  }, [lockRef, viewportRef]);

  useEffect(() => {
    const onWheel = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      if (lockRef?.current) return;
      applyZoom({
        nextZoom: zoomRef.current * getWheelZoomFactor(event),
        originClientX: event.clientX,
        originClientY: event.clientY,
      });
      lastOriginRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    return () =>
      window.removeEventListener("wheel", onWheel, { capture: true });
  }, [applyZoom, lockRef]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isNativeEditableTarget(event.target)) return;
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;
      const key = event.key;
      const code = event.code;
      if (
        key === "+" ||
        key === "=" ||
        code === "Equal" ||
        code === "NumpadAdd"
      ) {
        event.preventDefault();
        onZoomIn();
        return;
      }
      if (
        key === "-" ||
        key === "_" ||
        code === "Minus" ||
        code === "NumpadSubtract"
      ) {
        event.preventDefault();
        onZoomOut();
        return;
      }
      if (key === "0" || code === "Digit0" || code === "Numpad0") {
        event.preventDefault();
        onFit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onFit, onZoomIn, onZoomOut]);

  return {
    zoom,
    viewportSize,
    isPanning,
    spaceHeld,
    onZoomIn,
    onZoomOut,
    onFit,
    zoomTo,
    centerOn,
  };
};
